import type { Action, ActionAuthType, ActionCreate, ActionHttpMethod, ActionIntegration, ActionUpdate } from "../shared/types";

/** Keyword subset the backend validator accepts (app/services/actions/schema.py). */
export const SUPPORTED_KEYWORDS = new Set([
  "type", "properties", "required", "additionalProperties", "enum", "const", "minLength", "maxLength",
  "pattern", "format", "minimum", "maximum", "items", "maxItems", "description", "title",
]);
export const PARAM_TYPES = ["string", "number", "integer", "boolean", "date"] as const;
export type ParamType = (typeof PARAM_TYPES)[number];
export const HTTP_METHODS: readonly ActionHttpMethod[] = ["GET", "POST", "PUT", "PATCH"];
export const AUTH_TYPES: readonly ActionAuthType[] = ["none", "api_key", "bearer", "hmac"];
export const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]{1,62}$/;
export const PARAM_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/** `$.a.b`, `[0]` (one item) and `[*]` (every item of a list, up to MAPPING_LIST_MAX): mirrors ACTION_JSON_PATH_PATTERN. */
export const JSON_PATH_PATTERN = /^\$(\.[A-Za-z0-9_-]{1,64}|\[\d{1,4}\]|\[\*\])*$/;
/** A `[*]` mapping passes on at most this many values. */
export const MAPPING_LIST_MAX = 50;
/** The platform's own result key (it marks a list that was cut); a mapping field can't use it. */
export const RESERVED_MAPPING_FIELD = "_truncated";
/** "When to use" is capped server-side (WhenToUse); the form counts against the same limit. */
export const WHEN_TO_USE_MAX = 2000;
export const TIMEOUT_MIN_MS = 1000;
export const TIMEOUT_MAX_MS = 30_000;
export const DEFAULT_TIMEOUT_MS = 8000;
export const DEFAULT_AUDIENCES: readonly number[] = [1];
const PLACEHOLDER = /\{\{\s*([A-Za-z_][A-Za-z0-9_]{0,63})\s*\}\}/g;
const TOOL_NAME_MAX = 63;

export interface ParamRow {
  name: string;
  type: ParamType;
  required: boolean;
  description: string;
}

export interface KeyValueRow {
  name: string;
  value: string;
}

export interface MappingRow {
  field: string;
  path: string;
}

export interface JsonSchemaObject {
  type: "object";
  properties: Record<string, Record<string, unknown>>;
  required?: string[];
  additionalProperties?: boolean;
}

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** "Check enrollment status" → "check_enrollment_status". */
export function toolNameFrom(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const prefixed = /^[a-z]/.test(slug) ? slug : `action_${slug}`;
  return prefixed.slice(0, TOOL_NAME_MAX).replace(/_+$/, "");
}

export function rowsToSchema(rows: readonly ParamRow[]): JsonSchemaObject {
  const properties: JsonSchemaObject["properties"] = {};
  for (const row of rows) {
    const base: Json = row.type === "date" ? { type: "string", format: "date" } : { type: row.type };
    if (row.description.trim()) base.description = row.description.trim();
    properties[row.name.trim()] = base;
  }
  const required = rows.filter((r) => r.required).map((r) => r.name.trim());
  return { type: "object", properties, ...(required.length ? { required } : {}), additionalProperties: false };
}

function rowType(spec: Json): ParamType | null {
  if (spec.type === "string" && spec.format === "date") return "date";
  if (spec.format !== undefined) return null;
  return typeof spec.type === "string" && (PARAM_TYPES as readonly string[]).includes(spec.type) && spec.type !== "date" ? (spec.type as ParamType) : null;
}

/** Table rows for a schema, or `null` when it uses rules the table can't show (enum, pattern…). */
export function schemaToRows(schema: unknown): ParamRow[] | null {
  if (!isObject(schema) || schema.type !== "object") return null;
  const properties = isObject(schema.properties) ? schema.properties : {};
  const required = new Set(Array.isArray(schema.required) ? schema.required.map(String) : []);
  const rows: ParamRow[] = [];
  for (const [name, spec] of Object.entries(properties)) {
    if (!isObject(spec)) return null;
    const extra = Object.keys(spec).filter((k) => !["type", "format", "description"].includes(k));
    const type = rowType(spec);
    if (extra.length > 0 || !type) return null;
    rows.push({ name, type, required: required.has(name), description: typeof spec.description === "string" ? spec.description : "" });
  }
  return rows;
}

function keywordsSupported(schema: Json): boolean {
  if (Object.keys(schema).some((k) => !SUPPORTED_KEYWORDS.has(k))) return false;
  const children = isObject(schema.properties) ? Object.values(schema.properties) : [];
  if (isObject(schema.items)) children.push(schema.items);
  return children.every((child) => isObject(child) && keywordsSupported(child));
}

/** Parses the JSON editor text; `null` when invalid or outside the supported subset. */
export function parseSchemaJson(text: string): JsonSchemaObject | null {
  try {
    const value: unknown = JSON.parse(text);
    if (!isObject(value) || value.type !== "object" || !keywordsSupported(value)) return null;
    if (value.properties !== undefined && !isObject(value.properties)) return null;
    return value as unknown as JsonSchemaObject;
  } catch {
    return null;
  }
}

export function rowsValid(rows: readonly ParamRow[]): boolean {
  const names = rows.map((r) => r.name.trim());
  return names.every((n) => PARAM_NAME_PATTERN.test(n)) && new Set(names).size === names.length;
}

export function urlPlaceholders(url: string): string[] {
  return [...url.matchAll(PLACEHOLDER)].map((m) => m[1] ?? "");
}

/** https only, no credentials, no placeholders in the host, every placeholder a parameter. */
export function urlValid(url: string, paramNames: readonly string[]): boolean {
  const trimmed = url.trim();
  if (!trimmed.startsWith("https://")) return false;
  const authority = trimmed.slice("https://".length).split(/[/?#]/)[0] ?? "";
  if (!authority || /[{}@\s]/.test(authority)) return false;
  try {
    new URL(trimmed.replace(PLACEHOLDER, "x"));
  } catch {
    return false;
  }
  return urlPlaceholders(trimmed).every((name) => paramNames.includes(name));
}

export function mappingValid(rows: readonly MappingRow[]): boolean {
  return rows.every((r) => PARAM_NAME_PATTERN.test(r.field.trim()) && r.field.trim() !== RESERVED_MAPPING_FIELD && JSON_PATH_PATTERN.test(r.path.trim()));
}

export interface ActionFormValues {
  name: string;
  tool_name: string;
  when_to_use: string;
  enabled: boolean;
  requires_confirmation: boolean;
  requires_identity: boolean;
  /** Who may use the action (audience codes); the agent's own limit applies on top. */
  audiences: number[];
  params: ParamRow[];
  jsonMode: boolean;
  schemaJson: string;
  method: ActionHttpMethod;
  url: string;
  headers: KeyValueRow[];
  auth_type: ActionAuthType;
  auth_header: string;
  secret: string;
  timeout_ms: number;
  mapping: MappingRow[];
}

/** The schema the form currently describes (JSON editor or table). */
export function formSchema(values: Pick<ActionFormValues, "jsonMode" | "schemaJson" | "params">): JsonSchemaObject | null {
  return values.jsonMode ? parseSchemaJson(values.schemaJson) : rowsToSchema(values.params);
}

export function formValuesFrom(action: Action): ActionFormValues {
  const rows = schemaToRows(action.parameters_schema);
  return {
    name: action.name,
    tool_name: action.tool_name,
    when_to_use: action.when_to_use,
    enabled: action.enabled,
    requires_confirmation: action.requires_confirmation,
    requires_identity: action.requires_identity,
    audiences: [...action.audiences],
    params: rows ?? [],
    jsonMode: rows === null,
    schemaJson: JSON.stringify(action.parameters_schema, null, 2),
    method: (action.http?.method ?? "POST") as ActionHttpMethod,
    url: action.http?.url ?? "",
    headers: action.http?.headers.map((h) => ({ name: h.name, value: h.value })) ?? [],
    auth_type: action.auth_type as ActionAuthType,
    auth_header: action.auth_header_name ?? "",
    secret: "",
    timeout_ms: action.timeout_ms,
    mapping: action.response_mapping.map((m) => ({ field: m.field, path: m.path })),
  };
}

export interface TemplateDefaults {
  integration: ActionIntegration;
  name: string;
  method: ActionHttpMethod;
  url: string;
}

/**
 * "Confirm before running" starts ON for methods that change something on the other side (POST, PUT, PATCH)
 * and OFF for a read-only GET lookup such as "list the items". It is only a starting point: the editor keeps
 * the user's own choice once they have toggled it, and saved actions are never changed by it.
 */
export function confirmationDefault(method: ActionHttpMethod): boolean {
  return method !== "GET";
}

/** A new action starts open to whoever the agent is open to (`agentAudiences`). */
export function emptyFormValues(template: TemplateDefaults, agentAudiences: readonly number[] = DEFAULT_AUDIENCES): ActionFormValues {
  return {
    name: template.name, tool_name: toolNameFrom(template.name), when_to_use: "", enabled: true,
    requires_confirmation: confirmationDefault(template.method), requires_identity: false, audiences: [...agentAudiences], params: [], jsonMode: false,
    schemaJson: JSON.stringify(rowsToSchema([]), null, 2), method: template.method, url: template.url,
    headers: [], auth_type: "none", auth_header: "", secret: "", timeout_ms: DEFAULT_TIMEOUT_MS, mapping: [],
  };
}

function httpBody(values: ActionFormValues) {
  return {
    method: values.method,
    url: values.url.trim(),
    headers: values.headers.filter((h) => h.name.trim()).map((h) => ({ name: h.name.trim(), value: h.value })),
  };
}

function authBody(values: ActionFormValues) {
  return {
    type: values.auth_type,
    header_name: values.auth_type === "api_key" && values.auth_header.trim() ? values.auth_header.trim() : null,
    secret: values.auth_type !== "none" && values.secret ? values.secret : null,
  };
}

export function toCreate(values: ActionFormValues, integration: ActionIntegration, schema: JsonSchemaObject): ActionCreate {
  return {
    name: values.name.trim(), tool_name: values.tool_name.trim(), when_to_use: values.when_to_use.trim(),
    integration, enabled: values.enabled, parameters_schema: { ...schema }, http: httpBody(values),
    auth: authBody(values), response_mapping: values.mapping.map((m) => ({ field: m.field.trim(), path: m.path.trim() })),
    requires_identity: values.requires_identity, requires_confirmation: values.requires_confirmation,
    timeout_ms: values.timeout_ms, audiences: [...values.audiences].sort((a, b) => a - b),
  };
}

/** PATCH body: behaviour fields always; HTTP fields only for custom actions. */
export function toUpdate(values: ActionFormValues, action: Action, schema: JsonSchemaObject | null): ActionUpdate {
  const base: ActionUpdate = {
    name: values.name.trim(), when_to_use: values.when_to_use.trim(), enabled: values.enabled,
    requires_identity: values.requires_identity, requires_confirmation: values.requires_confirmation,
    // "Talk to a person" is open to everyone: the server refuses a change.
    ...(action.locked ? {} : { audiences: [...values.audiences].sort((a, b) => a - b) }),
  };
  if (action.type !== "http" || !schema) return base;
  return {
    ...base, tool_name: values.tool_name.trim(), parameters_schema: { ...schema }, http: httpBody(values),
    auth: authBody(values), timeout_ms: values.timeout_ms,
    response_mapping: values.mapping.map((m) => ({ field: m.field.trim(), path: m.path.trim() })),
  };
}

/** Sample test input: one example value per parameter. */
export function sampleInput(schema: JsonSchemaObject | null): string {
  const sample: Json = {};
  for (const [name, spec] of Object.entries(schema?.properties ?? {})) {
    if (spec.format === "date") sample[name] = "2026-01-31";
    else if (spec.type === "number" || spec.type === "integer") sample[name] = 1;
    else if (spec.type === "boolean") sample[name] = false;
    else sample[name] = "";
  }
  return JSON.stringify(sample, null, 2);
}

/** Card badge key by built-in tool name. */
export function builtinBadge(toolName: string): string {
  if (toolName === "capture_lead") return "builtinLeads";
  if (toolName === "human_handoff") return "builtinInbox";
  if (toolName === "send_checklist_email") return "builtinEmail";
  if (toolName.startsWith("book_") || toolName.startsWith("schedule_")) return "builtinBooking";
  return "builtin";
}
